import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  ExternalLink,
  Shield,
  PawPrint,
  HandHeart,
  Sparkles,
  Copy,
  Check,
  Server,
  Database,
  Gavel,
  Cpu,
  X,
  Smartphone,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import StripeDonateCard from "../components/StripeDonateCard";
import StripePaymentLinkButton from "../components/StripePaymentLinkButton";

// Owner-operated direct-support rails (Cash App / Zelle)
// These keep the lights on for Paws on Record itself:
//   - LLM API costs for the news ingestor
//   - Geocoding + map tile fees
//   - Hosting + database backups
//   - Legal/takedown review time
const SUPPORT_RAILS = {
  cashapp: {
    handle: "$imnono7733",
    // Cash App universal-link pattern: cash.app/$handle/AMOUNT opens the Cash
    // App native app on iOS/Android (pre-filled with amount) and falls back
    // to the Cash App web checkout on desktop.
    baseUrl: "https://cash.app/$imnono7733",
    label: "Cash App",
  },
  zelle: {
    handle: "info@pawsonrecord.com",
    // Zelle does not publish a reliable universal deep-link scheme —
    // payments must originate inside the sender's own banking app.
    label: "Zelle",
  },
};

const CASHAPP_PRESETS = [5, 10, 25, 50, 100];

const WHY_ITEMS = [
  {
    icon: Cpu,
    title: "AI news ingestor",
    body:
      "Every convicted-abuser record gets parsed by an LLM from a verified news source. Each URL we ingest costs tokens.",
  },
  {
    icon: Database,
    title: "Geocoding & maps",
    body:
      "The 50-mile radius search and nearest-police lookup hit geocoding and OpenStreetMap services that have daily quotas.",
  },
  {
    icon: Server,
    title: "Hosting & backups",
    body:
      "Servers, database, video-evidence object storage, and daily off-site backups so no record is ever lost.",
  },
  {
    icon: Gavel,
    title: "Takedown review",
    body:
      "Every takedown / correction request gets reviewed by a human against the underlying court record — that time isn't free.",
  },
];

const ORGS = [
  {
    name: "ASPCA",
    subtitle: "American Society for the Prevention of Cruelty to Animals",
    url: "https://secure.aspca.org/donate/donate",
    blurb:
      "Founded 1866. Rescues animals from cruelty, runs an animal-poison control hotline, and advocates for stronger laws in every state.",
    tag: "Direct rescue · Advocacy",
  },
  {
    name: "Humane Society of the U.S. (HSUS)",
    subtitle: "National animal-protection non-profit",
    url: "https://secure.humanesociety.org/site/Donation2",
    blurb:
      "Investigates cruelty operations (puppy mills, fighting rings), supports legislation, and deploys response teams to major cruelty busts.",
    tag: "Investigations · Legislation",
  },
  {
    name: "Best Friends Animal Society",
    subtitle: "No-kill shelter network",
    url: "https://bestfriends.org/donate",
    blurb:
      "Runs the nation's largest no-kill sanctuary and coordinates local shelters to eliminate shelter killing across the United States.",
    tag: "Shelter rescue · No-kill",
  },
  {
    name: "Animal Legal Defense Fund",
    subtitle: "Prosecutorial & legal advocacy",
    url: "https://aldf.org/donate/",
    blurb:
      "Funds the prosecution of animal cruelty cases, pushes for stronger anti-cruelty laws, and maintains the U.S. State Rankings for animal-protection laws.",
    tag: "Legal advocacy",
  },
  {
    name: "RedRover",
    subtitle: "Emergency sheltering & crisis response",
    url: "https://redrover.org/donate/",
    blurb:
      "Deploys volunteers to shelter animals rescued from puppy mills, hoarding cases, natural disasters, and domestic-violence situations.",
    tag: "Emergency response",
  },
  {
    name: "Alley Cat Allies",
    subtitle: "Community cat welfare",
    url: "https://secure.alleycat.org/give",
    blurb:
      "Leads the national movement to protect community cats, advocating against cruelty and for humane Trap-Neuter-Return programs.",
    tag: "Community cats",
  },
];

export default function Donate() {
  const [copied, setCopied] = useState(null);
  const [cashAmount, setCashAmount] = useState(25);
  const [cashCustom, setCashCustom] = useState("");
  const [zelleOpen, setZelleOpen] = useState(false);

  const handleCopy = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    toast.success(`Copied ${value}`);
    setTimeout(() => setCopied(null), 1800);
  };

  const activeCashAmount = () => {
    const custom = parseFloat(cashCustom);
    if (!isNaN(custom) && custom > 0) return Math.min(Math.round(custom), 5000);
    return cashAmount;
  };

  const openCashApp = () => {
    const amt = activeCashAmount();
    // Cash App universal link: /$handle/amount deep-links into the native app
    // on iOS + Android (via Universal Links / App Links) and falls back to
    // Cash App's web pay page on desktop browsers.
    const url = `${SUPPORT_RAILS.cashapp.baseUrl}/${amt}`;
    // Open in a new tab so we don't lose the donate page; on mobile this hop
    // is what triggers the OS to launch the installed Cash App app.
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="donate-page">
      <SEO
        title="Donate · Keep the U.S. Animal Abuser Registry Free"
        description="Paws on Record is a one-person operation. Every $5 keeps the database live another day; every $100 sponsors a week. Donate via Cash App, Zelle, or to one of six verified 501(c)(3) animal-protection allies."
        path="/donate"
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="label-eyebrow">Section · Donate</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
            Keep this registry
            <br />
            alive — or{" "}
            <span className="relative inline-block">
              <span className="relative z-10">fund the fight.</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-red-600/90 -z-0" />
            </span>
          </h1>
          <p className="mt-6 text-lg text-zinc-700 max-w-2xl leading-relaxed">
            Two ways to give. Send a direct tip to keep Paws on Record running,
            ingesting new convictions, and processing takedown requests — or
            donate to one of the verified 501(c)(3) rescue orgs below that do
            the on-the-ground work.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 border border-zinc-900 bg-white px-3 py-2 text-[11px] uppercase tracking-widest font-bold">
            <Shield size={12} /> Rescue-org links go direct — we take $0 of those
          </div>
        </div>
        <div className="lg:col-span-5">
          <StripePaymentLinkButton testId="donate-page-stripe-link" className="mb-6" />
          <StripeDonateCard />
          <div className="mt-6 border border-zinc-900 bg-zinc-900 text-white p-6" data-testid="support-owner-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border border-red-600 bg-red-600 flex items-center justify-center">
                <Heart size={18} />
              </div>
              <div className="label-eyebrow text-red-500">Or send a direct tip</div>
            </div>
            <div className="font-display font-black text-xl mt-4 leading-tight">
              Cash App / Zelle (no fees)
            </div>
            <p className="text-xs text-zinc-300 mt-2 leading-relaxed">
              Prefer not to use a card? Tap to copy — paste into Cash App or your
              banking app.
            </p>

            <div className="mt-5 space-y-4">
              {/* CASH APP — primary action with amount picker + real Donate CTA */}
              <div
                className="border border-zinc-700 bg-zinc-800/60 p-4"
                data-testid="cashapp-row"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 border border-emerald-500 text-emerald-400 flex items-center justify-center text-sm font-black">
                    $
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                      {SUPPORT_RAILS.cashapp.label} — one tap
                    </div>
                    <div className="font-mono text-sm" data-testid="cashapp-handle">
                      {SUPPORT_RAILS.cashapp.handle}
                    </div>
                  </div>
                  <button
                    onClick={() => handleCopy("cashapp", SUPPORT_RAILS.cashapp.handle)}
                    className="p-2 border border-zinc-700 hover:border-red-500 hover:text-red-400 transition-colors"
                    aria-label="Copy Cash App handle"
                    data-testid="cashapp-copy-btn"
                  >
                    {copied === "cashapp" ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-5 gap-1.5">
                  {CASHAPP_PRESETS.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        setCashAmount(v);
                        setCashCustom("");
                      }}
                      data-testid={`cashapp-amount-${v}`}
                      className={`py-2 text-xs font-black tracking-tight border transition-colors ${
                        cashAmount === v && !cashCustom
                          ? "bg-emerald-500 border-emerald-500 text-zinc-900"
                          : "border-zinc-700 text-zinc-200 hover:border-emerald-500"
                      }`}
                    >
                      ${v}
                    </button>
                  ))}
                </div>

                <div className="mt-2 flex items-center gap-2 border border-zinc-700 bg-zinc-900/50 px-2">
                  <DollarSign size={12} className="text-zinc-500" />
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    max="5000"
                    placeholder="Custom amount"
                    value={cashCustom}
                    onChange={(e) => setCashCustom(e.target.value)}
                    className="flex-1 bg-transparent py-2 text-sm outline-none placeholder:text-zinc-500"
                    data-testid="cashapp-custom-input"
                  />
                </div>

                <button
                  type="button"
                  onClick={openCashApp}
                  className="mt-3 w-full bg-emerald-500 text-zinc-900 hover:bg-emerald-400 transition-colors py-3 text-sm font-black uppercase tracking-widest inline-flex items-center justify-center gap-2"
                  data-testid="cashapp-donate-btn"
                >
                  <Smartphone size={16} /> Donate ${activeCashAmount()} with Cash App
                </button>
                <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
                  Opens the Cash App mobile app when installed, or Cash App web checkout on desktop.
                </p>
              </div>

              {/* ZELLE — primary action opens a step-by-step instructions modal */}
              <div
                className="border border-zinc-700 bg-zinc-800/60 p-4"
                data-testid="zelle-row"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 border border-purple-400 text-purple-300 flex items-center justify-center text-sm font-black">
                    Z
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                      {SUPPORT_RAILS.zelle.label} — pay from your bank
                    </div>
                    <div className="font-mono text-sm" data-testid="zelle-handle">
                      {SUPPORT_RAILS.zelle.handle}
                    </div>
                  </div>
                  <button
                    onClick={() => handleCopy("zelle", SUPPORT_RAILS.zelle.handle)}
                    className="p-2 border border-zinc-700 hover:border-red-500 hover:text-red-400 transition-colors"
                    aria-label="Copy Zelle address"
                    data-testid="zelle-copy-btn"
                  >
                    {copied === "zelle" ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setZelleOpen(true)}
                  className="mt-3 w-full bg-purple-500 text-white hover:bg-purple-400 transition-colors py-3 text-sm font-black uppercase tracking-widest inline-flex items-center justify-center gap-2"
                  data-testid="zelle-donate-btn"
                >
                  <HandHeart size={16} /> Donate with Zelle
                </button>
                <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
                  Zelle payments must be sent from inside your bank's app — we'll show the recipient info + steps.
                </p>
              </div>

              <p className="text-[11px] text-zinc-500 leading-relaxed mt-2">
                Tips to the registry aren't tax-deductible — if you need a
                receipt, donate to one of the 501(c)(3)s below instead.
              </p>
            </div>
          </div>

          {/* ZELLE INSTRUCTIONS MODAL */}
          {zelleOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/80 backdrop-blur-sm px-4"
              onClick={() => setZelleOpen(false)}
              data-testid="zelle-modal-backdrop"
            >
              <div
                className="w-full max-w-md bg-white border border-zinc-900 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="zelle-modal"
              >
                <div className="flex items-center justify-between bg-zinc-900 text-white px-5 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 border border-purple-400 text-purple-300 flex items-center justify-center text-xs font-black">
                      Z
                    </div>
                    <div className="font-display font-black text-sm uppercase tracking-widest">
                      Donate with Zelle
                    </div>
                  </div>
                  <button
                    onClick={() => setZelleOpen(false)}
                    aria-label="Close"
                    className="p-1 hover:text-red-400"
                    data-testid="zelle-modal-close"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="p-5">
                  <div className="label-eyebrow">Send to</div>
                  <div className="mt-2 flex items-center justify-between border border-zinc-900 bg-zinc-50 px-3 py-3">
                    <div className="font-mono text-sm font-bold text-zinc-900 truncate" data-testid="zelle-modal-recipient">
                      {SUPPORT_RAILS.zelle.handle}
                    </div>
                    <button
                      onClick={() => handleCopy("zelle-modal", SUPPORT_RAILS.zelle.handle)}
                      className="flex-shrink-0 ml-3 px-3 py-2 border border-zinc-900 bg-white text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white transition-colors inline-flex items-center gap-1.5"
                      data-testid="zelle-modal-copy-btn"
                    >
                      {copied === "zelle-modal" ? (
                        <>
                          <Check size={12} /> Copied
                        </>
                      ) : (
                        <>
                          <Copy size={12} /> Copy
                        </>
                      )}
                    </button>
                  </div>
                  <div className="mt-1 text-[11px] text-zinc-500">
                    Recipient name shown in Zelle: <span className="font-mono">Paws on Record</span>
                  </div>

                  <div className="mt-5 label-eyebrow">How to send</div>
                  <ol className="mt-2 space-y-2 text-sm text-zinc-800 leading-relaxed">
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-5 h-5 bg-zinc-900 text-white text-[11px] font-black flex items-center justify-center">1</span>
                      <span>Open your bank's mobile app (Chase, BofA, Wells Fargo, etc.) and go to <b>Zelle</b> or <b>Send Money</b>.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-5 h-5 bg-zinc-900 text-white text-[11px] font-black flex items-center justify-center">2</span>
                      <span>Paste the copied email <span className="font-mono">{SUPPORT_RAILS.zelle.handle}</span> as the recipient.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-5 h-5 bg-zinc-900 text-white text-[11px] font-black flex items-center justify-center">3</span>
                      <span>Enter the amount you want to donate. Add "Paws on Record" in the memo (optional).</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex-shrink-0 w-5 h-5 bg-zinc-900 text-white text-[11px] font-black flex items-center justify-center">4</span>
                      <span>Confirm and send — the payment goes bank-to-bank with no fees.</span>
                    </li>
                  </ol>

                  <div className="mt-5 border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 text-[11px] text-zinc-600 leading-relaxed">
                    Zelle doesn't support a universal payment link — that's why donations must originate inside your own banking app. First-time Zelle senders may see a small verification step from their bank.
                  </div>

                  <button
                    type="button"
                    onClick={() => setZelleOpen(false)}
                    className="mt-5 w-full py-3 bg-zinc-900 text-white text-sm font-black uppercase tracking-widest hover:bg-red-600 transition-colors"
                    data-testid="zelle-modal-done"
                  >
                    Got it
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Why donations matter */}
      <div className="mt-14 border border-zinc-900 bg-white p-8" data-testid="why-donate-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="label-eyebrow">Why it matters</div>
            <h2 className="font-display font-black text-3xl tracking-tight mt-2 leading-tight">
              Donations keep this
              <br />
              registry up to date.
            </h2>
            <p className="text-sm text-zinc-700 mt-4 leading-relaxed">
              Paws on Record isn't a charity and doesn't take a salary — but it
              does cost real money to run. Every dollar goes straight into
              keeping the data current, accurate, and legally defensible so
              shelters, fosters, and rescues can trust what they see here.
            </p>
            <Link
              to="/where-your-money-goes"
              className="inline-flex items-center gap-1.5 mt-4 text-xs font-bold uppercase tracking-widest text-red-600 hover:text-red-700 underline"
              data-testid="donate-transparency-link"
            >
              See every line item →
            </Link>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {WHY_ITEMS.map((w) => {
              const Icon = w.icon;
              return (
                <div
                  key={w.title}
                  className="border border-zinc-300 p-5 hover-shadow-harsh"
                  data-testid={`why-item-${w.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="w-9 h-9 border border-zinc-900 bg-zinc-900 text-white flex items-center justify-center">
                    <Icon size={16} />
                  </div>
                  <div className="font-display font-black text-lg tracking-tight mt-3">
                    {w.title}
                  </div>
                  <p className="text-sm text-zinc-700 mt-2 leading-relaxed">
                    {w.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-14">
        <div className="label-eyebrow">Verified 501(c)(3) allies</div>
        <h2 className="font-display font-black text-3xl tracking-tight mt-2">
          Prefer a tax-deductible gift? Give directly to one of these.
        </h2>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="donate-orgs">
        {ORGS.map((o, i) => (
          <a
            key={o.name}
            href={o.url}
            target="_blank"
            rel="noreferrer"
            className="border border-zinc-300 bg-white p-6 hover-shadow-harsh fade-up block"
            style={{ animationDelay: `${i * 60}ms` }}
            data-testid={`donate-org-${i}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="w-10 h-10 border border-zinc-900 bg-zinc-900 text-white flex items-center justify-center">
                <PawPrint size={18} />
              </div>
              <ExternalLink size={16} className="text-zinc-400" />
            </div>
            <div className="mt-4 label-eyebrow">{o.tag}</div>
            <h3 className="font-display font-black text-xl tracking-tight mt-1 leading-tight">
              {o.name}
            </h3>
            <div className="text-[11px] font-mono text-zinc-500 mt-1">{o.subtitle}</div>
            <p className="text-sm text-zinc-700 mt-4 leading-relaxed">{o.blurb}</p>
            <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold">
              <HandHeart size={14} /> Donate direct →
            </div>
          </a>
        ))}
      </div>

      <div className="mt-16 border border-zinc-900 p-8 bg-zinc-50">
        <div className="flex items-start gap-4">
          <Sparkles size={22} className="flex-shrink-0 text-red-600 mt-1" />
          <div>
            <div className="label-eyebrow">Another way to help</div>
            <div className="font-display font-black text-2xl tracking-tight mt-1">
              Report abuse. Share a case. Contact your legislator.
            </div>
            <p className="text-sm text-zinc-700 mt-3 max-w-2xl leading-relaxed">
              Money isn't the only input that matters. A single 911 call can
              save an animal's life. A share on social media can raise a case's
              profile. An email to your state rep can change a law.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/report" className="btn-danger" data-testid="donate-report-cta">
                Report Abuse
              </Link>
              <Link to="/" className="btn-outline" data-testid="donate-registry-cta">
                Browse Registry
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
